<template>
  <div class="position-relative" id="examResultSingle">
    <div class="d-flex align-items-center justify-content-between mb-3 d-print-none">
      <div>
        <h4 class="mb-0">{{ classStudent.first_name }} {{ classStudent.last_name }}</h4>
        <small class="text-muted">
          Roll: <strong class="text-dark">{{ studentData.roll_no || '--' }}</strong> |
          Code: <strong class="text-dark">{{ studentData.student_code || '--' }}</strong> |
          Class: <strong class="text-dark">{{ examData.school_class.name }}</strong> |
          Section: <strong class="text-dark">{{ examData.section.name }}</strong>
        </small>
      </div>
      <div class="d-flex align-items-center">
        <a-radio-group v-model="viewMode" button-style="solid" size="small" class="mr-2">
          <a-radio-button value="mobile">
            <i class="fe fe-smartphone mr-1"></i> Parent Mobile View
          </a-radio-button>
          <a-radio-button value="print">
            <i class="fe fe-file-text mr-1"></i> Printable Marksheet
          </a-radio-button>
        </a-radio-group>
        <button
          v-if="viewMode === 'print'"
          type="button"
          class="btn btn-light btn-sm btn-with-addon"
          @click="print"
        >
          <span class="btn-addon">
            <i class="btn-addon-icon fe fe-printer" />
          </span>
          Print
        </button>
      </div>
    </div>

    <!-- ───────────────────────────────────────────────────────────── -->
    <!-- 1. PARENT MOBILE APP VIEW SIMULATOR -->
    <!-- ───────────────────────────────────────────────────────────── -->
    <div v-if="viewMode === 'mobile'" class="mobile-simulator-wrapper">
      <div class="mobile-device-frame">
        <div class="mobile-device-header">
          <div class="d-flex align-items-center justify-content-between text-muted small px-3 pt-2">
            <span><i class="fe fe-smartphone mr-1"></i> Parent Mobile Preview</span>
            <span class="badge badge-success px-2 py-1">Live Sync</span>
          </div>
          <div class="px-3 pt-2 pb-1">
            <div class="mobile-student-title">Exam result for {{ classStudent.first_name }} {{ classStudent.last_name }}</div>
          </div>
        </div>

        <div class="mobile-device-body p-3">
          <!-- Exam Selector Banner -->
          <div class="mb-3">
            <div class="small font-weight-bold text-muted mb-1">Exam:</div>
            <div class="mobile-exam-selector">
              <span>{{ activeExamName }}</span>
              <i class="fe fe-chevron-down text-white"></i>
            </div>
          </div>

          <!-- Withheld Alert Banner -->
          <div v-if="isWithheld" class="mobile-withheld-container mb-3">
            <div class="mobile-withheld-title">Result Withheld</div>
            <div v-if="withheldRemark" class="mobile-withheld-remark">{{ withheldRemark }}</div>
          </div>

          <!-- Normal Exam Card (when not withheld) -->
          <div v-else-if="activeStudentExam" class="mobile-exam-card">
            <!-- Exam Summary Stats + Donut Chart -->
            <div class="mobile-exam-summary d-flex">
              <div class="summary-stats-col flex-grow-1">
                <div v-if="activeStudentExam.status !== 'Fail' && activeStudentExam.total_mark !== null" class="summary-item">
                  <span class="summary-label">Total Mark:</span>
                  <strong class="summary-val">{{ activeStudentExam.total_mark }}</strong>
                </div>
                <div v-if="activeStudentExam.status !== 'Fail' && activeStudentExam.average_mark !== null" class="summary-item">
                  <span class="summary-label">Average:</span>
                  <strong class="summary-val">{{ activeStudentExam.average_mark }}</strong>
                </div>
                <div v-if="activeStudentExam.status" class="summary-item">
                  <span class="summary-label">Status:</span>
                  <strong :class="activeStudentExam.status === 'Pass' ? 'text-success' : 'text-danger'">
                    {{ activeStudentExam.status }}
                  </strong>
                </div>
                <div v-if="activeStudentExam.section_rank" class="summary-item">
                  <span class="summary-label">Section Rank:</span>
                  <strong class="summary-val">{{ activeStudentExam.section_rank }}</strong>
                </div>
                <div v-if="activeStudentExam.class_rank" class="summary-item">
                  <span class="summary-label">Class Rank:</span>
                  <strong class="summary-val">{{ activeStudentExam.class_rank }}</strong>
                </div>
                <div v-if="attendanceDisplay" class="summary-item">
                  <span class="summary-label">Attendance:</span>
                  <strong class="summary-val">{{ attendanceDisplay }}</strong>
                </div>
                <div v-if="activeStudentExam.attendance_percent !== null && activeStudentExam.attendance_percent !== undefined" class="summary-item">
                  <span class="summary-label">Attendance %:</span>
                  <strong class="summary-val">{{ activeStudentExam.attendance_percent }}%</strong>
                </div>
              </div>

              <!-- Radial Donut Chart -->
              <div class="summary-donut-col d-flex flex-column align-items-center justify-content-center pl-2">
                <a-progress
                  type="circle"
                  :percent="donutPercentage"
                  :width="82"
                  :stroke-width="12"
                  stroke-color="#1890ff"
                  :format="percent => activeStudentExam.status === 'Fail' ? 'Fail' : `${percent}%`"
                />
              </div>
            </div>

            <!-- Scholastic Results (Mark) -->
            <div v-if="scholasticMarks.length > 0" class="mt-3">
              <div class="mobile-section-header">
                Scholastic Results (Mark)
              </div>
              <table class="table table-sm mobile-marks-table mb-0">
                <thead>
                  <tr>
                    <th style="width: 45%;">Subject</th>
                    <th style="width: 18%; text-align: right;">Theory</th>
                    <th style="width: 18%; text-align: right;">Practical</th>
                    <th style="width: 19%; text-align: right;">Total</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="markObj in scholasticMarks" :key="markObj.subject_id">
                    <td class="font-weight-500">{{ getSubjectDisplayName(markObj.subject_id) }}</td>
                    <td class="text-right text-muted">{{ formatTheoryMark(markObj) }}</td>
                    <td class="text-right text-muted">{{ formatPracticalMark(markObj) }}</td>
                    <td class="text-right font-weight-bold" :class="{ 'text-danger': isMarkFailedOrAbsent(markObj) }">
                      {{ formatTotalMark(markObj) }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Co-Scholastic Results (Grade) -->
            <div v-if="coScholasticMarks.length > 0" class="mt-3">
              <div class="mobile-section-header">
                Co-Scholastic Results (Grade)
              </div>
              <table class="table table-sm mobile-marks-table mb-0">
                <thead>
                  <tr>
                    <th style="width: 60%;">Activity / Subject</th>
                    <th style="width: 40%; text-align: right;">Grade</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="markObj in coScholasticMarks" :key="markObj.subject_id">
                    <td class="font-weight-500">{{ getSubjectDisplayName(markObj.subject_id) }}</td>
                    <td class="text-right font-weight-bold text-primary" style="text-transform: uppercase;">
                      {{ markObj.grade || markObj.theory_grade || '-' }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Exam Note -->
            <div v-if="activeExamNote" class="mt-3 p-2 bg-light rounded small text-muted font-italic">
              <strong>Note:</strong> {{ activeExamNote }}
            </div>

            <!-- Download Report Card Button (LFS School only - matches Mobile App) -->
            <div v-if="hasReportCardDownload" class="mt-4">
              <button
                type="button"
                class="btn btn-primary btn-block mobile-download-btn shadow-sm"
                @click="downloadReportCard"
              >
                <i class="fe fe-file-text mr-2"></i> Download Report Card
              </button>
            </div>
          </div>

          <div v-else class="text-center py-4 text-muted">
            <i class="fe fe-info mr-1"></i> Exam data is not yet available for this student.
          </div>
        </div>
      </div>

      <!-- Remarks Management below the mobile frame -->
      <div class="mt-4">
        <h5 class="mb-2">Teacher Remarks</h5>
        <student-exam-remarks
          :student="studentData"
          :examData="examData"
          :remarksData="remarksData"
          @updateTable="handleRemarkTableUpdate"
        />
      </div>
    </div>

    <!-- ───────────────────────────────────────────────────────────── -->
    <!-- 2. TRADITIONAL PRINTABLE MARKSHEET VIEW -->
    <!-- ───────────────────────────────────────────────────────────── -->
    <div v-else>
      <neo-print-wrapper title="Exam Report">
        <a-spin :spinning="loading">
          <div class="row">
            <div class="col-12">
              <h4>{{ classStudent.first_name }} {{ classStudent.last_name }}</h4>
              <p class="mb-2">
                Student code: <span class="font-weight-bold">{{ studentData.student_code }}</span> |
                Class: <span class="font-weight-bold">{{ examData.school_class.name }}</span> |
                Section: <span class="font-weight-bold">{{ examData.section.name }}</span> |
                Roll No: <span class="font-weight-bold">{{ studentData.roll_no }}</span>
              </p>
            </div>
          </div>

          <div class="row">
            <div class="col-12">
              <vue-custom-scrollbar class="scroll-area">
                <table class="table table-sm table-sm-responsive table-bordered">
                  <thead>
                    <tr>
                      <th scope="col">Subject</th>
                      <th scope="col" style="text-align: right;">Theory</th>
                      <th scope="col" style="text-align: right;">Practical</th>
                      <th scope="col" style="text-align: right;">Total Score</th>
                      <th scope="col" style="text-align: center;">Grade</th>
                      <th scope="col" style="text-align: right;">Pass Mark</th>
                      <th scope="col" style="text-align: right;">Full Mark</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="examSubject in examData.class_section_exam_subject"
                      :key="examSubject.id"
                    >
                      <th scope="row">
                        {{ getSubjectDisplayName(examSubject.subject.id) }} ({{ examSubject.subject.abbreviation }})
                      </th>
                      <td class="text-right">
                        {{ getSubjectTheoryScore(examSubject.subject.id) }}
                      </td>
                      <td class="text-right">
                        {{ examSubject.has_practical ? getSubjectPracticalScore(examSubject.subject.id) : '-' }}
                      </td>
                      <td class="text-right px-2">
                        <span :class="getStudentSubjectMark(examSubject.subject.id) && isMarkFailedOrAbsent(getStudentSubjectMark(examSubject.subject.id)) ? 'text-danger font-weight-bold' : 'text-dark font-weight-bold'">
                          {{ getStudentScore(getStudentSubjectMark(examSubject.subject.id)) }}
                        </span>
                      </td>
                      <td class="text-center font-weight-bold">
                        {{ getSubjectGrade(examSubject.subject.id) }}
                      </td>
                      <td class="text-right px-2">{{ examSubject.pass_mark }}</td>
                      <td class="text-right px-2">{{ examSubject.full_mark }}</td>
                    </tr>
                  </tbody>
                </table>
              </vue-custom-scrollbar>
            </div>
          </div>

          <!-- Reports Summary Bar -->
          <div class="d-flex flex-wrap p-2 bg-light rounded mt-2" v-if="reportsData || activeStudentExam">
            <div class="col-6 col-md-3 mb-2 mb-md-0">
              <p class="mb-0">
                Score:
                <span class="ml-2 font-weight-bold">{{ evaluatedTotalScore }} / {{ totalFullMark }}</span>
              </p>
            </div>
            <div class="col-6 col-md-3 mb-2 mb-md-0">
              <p class="mb-0">
                Percentage:
                <span class="ml-2 font-weight-bold">{{ evaluatedPercentage }}%</span>
              </p>
            </div>
            <div class="col-6 col-md-3 mb-2 mb-md-0">
              <p class="mb-0">
                Attendance:
                <span class="ml-2 font-weight-bold">{{ evaluatedAttendance }}</span>
              </p>
            </div>
            <div class="col-6 col-md-3 mb-2 mb-md-0" v-if="evaluatedStatus">
              <p class="mb-0">
                Status:
                <span class="ml-2 font-weight-bold" :class="evaluatedStatus === 'Pass' ? 'text-success' : 'text-danger'">{{ evaluatedStatus }}</span>
              </p>
            </div>
            <div class="col-6 col-md-3 mt-md-2" v-if="rankSummaryText">
              <p class="mb-0">
                Rank:
                <span class="ml-2 font-weight-bold text-dark">{{ rankSummaryText }}</span>
              </p>
            </div>
          </div>

          <!-- Remarks -->
          <div class="row mt-3">
            <div class="col-12">
              <student-exam-remarks
                :student="studentData"
                :examData="examData"
                :remarksData="remarksData"
                @updateTable="handleRemarkTableUpdate"
              />
            </div>
          </div>
        </a-spin>
      </neo-print-wrapper>
    </div>
  </div>
</template>

<script>
import { find } from 'lodash'

import vueCustomScrollbar from 'vue-custom-scrollbar'
import NeoPrintWrapper from '@/components/layout/Print/Default'
import StudentExamRemarks from '../Remark/index'

export default {
  name: 'StudentMarksDisplaySingle',
  props: ['studentData', 'examData', 'reportsData', 'remarksData'],
  components: {
    vueCustomScrollbar,
    StudentExamRemarks,
    NeoPrintWrapper,
  },
  data: function() {
    return {
      loading: false,
      viewMode: 'mobile', // 'mobile' | 'print'
    }
  },
  computed: {
    classStudent: function() {
      return this.studentData.student || {}
    },
    studentMarks: function() {
      return this.studentData.student_exam_mark || []
    },
    activeExamResult() {
      return (this.reportsData && this.reportsData.exam_result) ? this.reportsData.exam_result : null
    },
    activeExamName() {
      if (this.activeExamResult && this.activeExamResult.exam) {
        return this.activeExamResult.exam.name
      }
      return this.examData && this.examData.exam ? this.examData.exam.name : 'Exam Result'
    },
    activeExamNote() {
      return this.activeExamResult ? this.activeExamResult.note : null
    },
    activeStudentExam() {
      if (this.activeExamResult && this.activeExamResult.student_exams && this.activeExamResult.student_exams.length > 0) {
        return this.activeExamResult.student_exams[0]
      }
      if (this.studentData && this.studentData.student_exam && this.studentData.student_exam.length > 0) {
        return this.studentData.student_exam[0]
      }
      return null
    },
    isWithheld() {
      if (this.reportsData && typeof this.reportsData.withheld !== 'undefined') {
        return Boolean(this.reportsData.withheld)
      }
      if (this.activeExamResult && typeof this.activeExamResult.withheld !== 'undefined') {
        return Boolean(this.activeExamResult.withheld)
      }
      return Boolean(this.studentData && this.studentData.is_withheld)
    },
    withheldRemark() {
      if (this.reportsData && this.reportsData.withheld_remark) {
        return this.reportsData.withheld_remark
      }
      if (this.activeExamResult && this.activeExamResult.withheld_remark) {
        return this.activeExamResult.withheld_remark
      }
      return this.studentData ? this.studentData.withheld_remark : ''
    },
    scholasticMarks() {
      if (this.activeStudentExam && this.activeStudentExam.student_exam_marks) {
        return this.activeStudentExam.student_exam_marks.filter(m => m.scholastic)
      }
      // Fallback from raw student marks
      return this.studentMarks.filter(m => !this.isCoScholasticSubject(m.subject_id))
    },
    coScholasticMarks() {
      if (this.activeStudentExam && this.activeStudentExam.student_exam_marks) {
        return this.activeStudentExam.student_exam_marks.filter(m => m.co_scholastic)
      }
      return this.studentMarks.filter(m => this.isCoScholasticSubject(m.subject_id))
    },
    donutPercentage() {
      if (this.activeStudentExam && this.activeStudentExam.mark_percentage !== null && this.activeStudentExam.mark_percentage !== undefined) {
        return parseFloat(this.activeStudentExam.mark_percentage)
      }
      if (this.reportsData && this.reportsData.percentage_score) {
        return parseFloat(this.reportsData.percentage_score)
      }
      return 0
    },
    attendanceDisplay() {
      if (!this.activeStudentExam) return '--'
      const att = this.activeStudentExam.attendance
      const totalDays = (this.reportsData && this.reportsData.total_working_days) || this.activeStudentExam.total_school_days
      if (att !== null && att !== undefined) {
        return totalDays ? `${att} / ${totalDays}` : `${att}`
      }
      return '--'
    },
    totalFullMark() {
      if (this.reportsData && this.reportsData.total_full_mark) {
        return this.reportsData.total_full_mark
      }
      if (this.examData && this.examData.class_section_exam_subject) {
        return this.examData.class_section_exam_subject.reduce((sum, s) => sum + (parseFloat(s.full_mark) || 0), 0)
      }
      return 0
    },
    evaluatedTotalScore() {
      if (this.activeStudentExam && this.activeStudentExam.total_mark !== null && this.activeStudentExam.total_mark !== undefined) {
        return this.activeStudentExam.total_mark
      }
      if (this.reportsData && this.reportsData.total_marks_scored !== null && this.reportsData.total_marks_scored !== undefined) {
        return this.reportsData.total_marks_scored
      }
      return '--'
    },
    evaluatedPercentage() {
      if (this.activeStudentExam && this.activeStudentExam.mark_percentage !== null && this.activeStudentExam.mark_percentage !== undefined) {
        return this.activeStudentExam.mark_percentage
      }
      if (this.reportsData && this.reportsData.percentage_score !== null && this.reportsData.percentage_score !== undefined) {
        return this.reportsData.percentage_score
      }
      return '--'
    },
    evaluatedAttendance() {
      return this.attendanceDisplay
    },
    hasReportCardDownload() {
      return Boolean(this.reportsData && this.reportsData.lfs_report_card_url)
    },
    evaluatedClassRank() {
      if (this.activeStudentExam && this.activeStudentExam.class_rank) {
        return this.activeStudentExam.class_rank
      }
      if (this.reportsData && this.reportsData.class_rank) {
        return this.reportsData.class_rank
      }
      return null
    },
    evaluatedSectionRank() {
      if (this.activeStudentExam && this.activeStudentExam.section_rank) {
        return this.activeStudentExam.section_rank
      }
      if (this.reportsData && this.reportsData.section_rank) {
        return this.reportsData.section_rank
      }
      return null
    },
    rankSummaryText() {
      const cRank = this.evaluatedClassRank
      const sRank = this.evaluatedSectionRank
      if (sRank && cRank) {
        return `Sec: ${sRank} | Class: ${cRank}`
      }
      if (cRank) {
        return `Class: ${cRank}`
      }
      if (sRank) {
        return `Sec: ${sRank}`
      }
      return null
    },
    evaluatedStatus() {
      if (this.activeStudentExam && this.activeStudentExam.status) {
        return this.activeStudentExam.status
      }
      if (this.reportsData && this.reportsData.status) {
        return this.reportsData.status
      }
      return null
    },
  },
  methods: {
    isCoScholasticSubject(subjectId) {
      if (this.examData && this.examData.class_section_exam_subject) {
        const item = this.examData.class_section_exam_subject.find(s => s.subject_id === subjectId)
        return item ? Boolean(item.grades_only) : false
      }
      return false
    },
    getSubjectDisplayName(subjectId) {
      if (this.activeExamResult && this.activeExamResult.class_section_exam_subject) {
        const item = this.activeExamResult.class_section_exam_subject.find(s => s.subject_id === subjectId)
        if (item && item.subject) {
          return item.subject.name
        }
      }
      if (this.examData && this.examData.class_section_exam_subject) {
        const item = this.examData.class_section_exam_subject.find(s => s.subject_id === subjectId)
        if (item && item.subject) {
          return item.subject.name
        }
      }
      return 'Subject'
    },
    getStudentSubjectMark(subjectId) {
      if (this.activeStudentExam && this.activeStudentExam.student_exam_marks) {
        const found = this.activeStudentExam.student_exam_marks.find(m => m.subject_id === subjectId)
        if (found) return found
      }
      return find(this.studentMarks, { subject_id: subjectId })
    },
    formatTheoryMark(markObj) {
      if (!markObj) return '-'
      if (markObj.theory_is_absent === 1 || markObj.theory_mark === 'Ab') return 'Ab'
      if (markObj.theory_is_absent === 2 || markObj.theory_mark === 'SL') return 'SL'
      if (markObj.theory_mark !== null && markObj.theory_mark !== undefined) return markObj.theory_mark
      return '-'
    },
    formatPracticalMark(markObj) {
      if (!markObj) return '-'
      if (markObj.practical_is_absent === 1 || markObj.practical_mark === 'Ab') return 'Ab'
      if (markObj.practical_is_absent === 2 || markObj.practical_mark === 'SL') return 'SL'
      if (markObj.practical_mark !== null && markObj.practical_mark !== undefined) return markObj.practical_mark
      return '-'
    },
    formatTotalMark(markObj) {
      if (!markObj) return '-'
      if (markObj.is_absent === 1 || markObj.mark === 'Ab') return 'Ab'
      if (markObj.is_absent === 2 || markObj.mark === 'SL') return 'SL'
      if (markObj.mark !== null && markObj.mark !== undefined) return markObj.mark
      return '-'
    },
    isMarkFailedOrAbsent(markObj) {
      if (!markObj) return false
      return Boolean(markObj.is_failed || markObj.is_absent || markObj.mark === 'Ab' || markObj.mark === 'SL' || markObj.theory_mark === 'Ab' || markObj.theory_mark === 'SL')
    },
    getSubjectTheoryScore(subjectId) {
      const mark = this.getStudentSubjectMark(subjectId)
      return this.formatTheoryMark(mark)
    },
    getSubjectPracticalScore(subjectId) {
      const mark = this.getStudentSubjectMark(subjectId)
      return this.formatPracticalMark(mark)
    },
    getSubjectGrade(subjectId) {
      const mark = this.getStudentSubjectMark(subjectId)
      return mark ? (mark.grade || mark.theory_grade || mark.exam_mark_grade || '--') : '--'
    },
    getStudentScore(markData) {
      if (!markData) return '-'
      if (this.examData && this.examData.grades) {
        return markData.grade || markData.exam_mark_grade || '--'
      }
      return this.formatTotalMark(markData)
    },
    downloadReportCard() {
      const url = this.reportsData && this.reportsData.lfs_report_card_url
      if (url) {
        window.open(url, '_blank')
      } else {
        this.$message.info('Report card download is only available for LFS School when declared.')
      }
    },
    handleRemarkTableUpdate() {
      this.$emit('updateRemarksTable')
    },
    print() {
      this.$htmlToPaper('examResultSingle')
    },
  },
}
</script>

<style lang="scss" scoped>
.mobile-simulator-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
}

.mobile-device-frame {
  width: 100%;
  max-width: 440px;
  background: #ffffff;
  border-radius: 18px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
  border: 1px solid #e8e8e8;
  overflow: hidden;
  margin-bottom: 20px;
  transition: all 0.3s ease;
}

.mobile-device-header {
  background: #fafafa;
  border-bottom: 1px solid #f0f0f0;
}

.mobile-student-title {
  font-size: 14px;
  font-weight: 600;
  color: #333333;
}

.mobile-exam-selector {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #1890ff;
  color: #ffffff;
  padding: 10px 14px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 14px;
}

.mobile-withheld-container {
  padding: 16px;
  background-color: #fff3f3;
  border-radius: 8px;
  border: 1px solid #ffcdd2;
  text-align: center;
}

.mobile-withheld-title {
  font-size: 16px;
  color: #d32f2f;
  font-weight: bold;
  margin-bottom: 4px;
}

.mobile-withheld-remark {
  font-size: 13px;
  color: #d32f2f;
}

.mobile-exam-card {
  border-radius: 10px;
}

.mobile-exam-summary {
  background: #f8f9fa;
  border-radius: 10px;
  padding: 14px;
  border: 1px solid #edf0f2;
}

.summary-item {
  font-size: 13px;
  margin-bottom: 5px;
  color: #666666;
  display: flex;
  align-items: center;
}

.summary-label {
  width: 100px;
  color: #777777;
}

.summary-val {
  color: #222222;
}

.mobile-section-header {
  background: rgba(24, 144, 255, 0.08);
  padding: 6px 10px;
  border-radius: 4px;
  border-left: 4px solid #1890ff;
  font-weight: 700;
  font-size: 13px;
  color: #1890ff;
  margin-bottom: 6px;
}

.mobile-marks-table {
  font-size: 13px;
  th {
    border-top: none;
    border-bottom: 1px solid #e8e8e8;
    color: #666;
    font-size: 12px;
    padding: 6px 4px;
  }
  td {
    border-top: none;
    border-bottom: 1px solid #f0f0f0;
    padding: 8px 4px;
    vertical-align: middle;
  }
}

.mobile-download-btn {
  border-radius: 10px;
  padding: 10px;
  font-weight: 600;
  font-size: 14px;
}

.scroll-area {
  position: relative;
  margin: auto;
}
</style>
